import { getSessionId, hasSession, encryptPayload } from "./cryptoService";
import { handleServerFrame } from "./serverPush";
import { tokenOfThisTab } from "./thisTabLogin";

// Keep-alive channel for the crypto session's idle TTL (see api-auth-gateway's
// SessionHeartbeatHandler). Only genuine mouse/keyboard/scroll activity slides
// the TTL — an unlocked laptop left open with the tab visible but nobody
// touching it must still time out, so presence (tab visible) alone is
// deliberately NOT treated as "alive". A dropped/reconnecting WS never forces
// logout by itself; it only stops refreshing the TTL, so a genuinely
// abandoned tab still expires via its normal idle timeout.
//
// Must stay comfortably below gateway.session.ttl-seconds, whatever that's
// set to in the current environment (e.g. a short TTL used for testing) —
// override via REACT_APP_SESSION_PING_INTERVAL_MS if the deployed TTL is
// unusually low.
const ACTIVITY_THROTTLE_MS = Number(process.env.REACT_APP_SESSION_PING_INTERVAL_MS) || 10 * 1000;
const RECONNECT_DELAY_MS = 5 * 1000;

const ACTIVITY_EVENTS = ["click", "keydown", "mousemove", "scroll", "touchstart", "wheel"];

let ws = null;
let lastPingSent = 0;
let currentSessionId = null;
let started = false;

function wsUrlFor(sessionId) {
  const base = process.env.REACT_APP_GATEWAY_URL || "";
  return base.replace(/^http/, "ws") + `/ws/session?sessionId=${sessionId}`;
}


// Every frame is sealed with the crypto session's AES key, like every HTTP body: {"data": "<base64 iv||ciphertext>"}.
// If it cannot be sealed (no session key) nothing is sent: never a plain frame, the hello carries the login token.
async function sendSealed(socket, message) {
  try {
    const envelope = await encryptPayload(message);
    if (socket && socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify(envelope));
  } catch {
    /* the next activity or reconnect tries again */
  }
}

function connect() {
  if (!started) return;

  const sessionId = getSessionId();
  if (!sessionId || !hasSession()) return;

  currentSessionId = sessionId;

  try {
    ws = new WebSocket(wsUrlFor(sessionId));
  } catch {
    return;
  }

  const socket = ws;
  ws.onopen = async () => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      // Says which login this tab belongs to (in a frame, never in the URL, which gets logged). While any connection of a
      // login is open the gateway knows a tab is still there; when the last one ends, the login's slot in the
      // active-session limit is freed after a short grace time unless a connection comes back (a reload does).
      const token = tokenOfThisTab();      // this tab's own login, never another tab's (see thisTabLogin.js)
      lastPingSent = Date.now();
      if (token) await sendSealed(socket, { type: "hello", token });
      await sendSealed(socket, { type: "activity" });
    }
  };

  ws.onmessage = (event) => handleServerFrame(event.data);

  ws.onclose = () => {
    ws = null;
    setTimeout(() => {
      if (started && hasSession()) connect();
    }, RECONNECT_DELAY_MS);
  };

  ws.onerror = () => {
    // onclose fires right after and handles the reconnect attempt
  };
}

function pingActivity() {
  if (!started) return;
  if (document.visibilityState !== "visible") return;

  const now = Date.now();
  if (now - lastPingSent < ACTIVITY_THROTTLE_MS) return;
  lastPingSent = now;

  // Session rotated (e.g. MPIN reauth happened) since we connected — reconnect fresh.
  if (getSessionId() !== currentSessionId) {
    if (ws) ws.close();
    connect();
    return;
  }

  if (!ws && hasSession()) {
    connect();
    return;
  }

  if (ws && ws.readyState === WebSocket.OPEN) {
    sendSealed(ws, { type: "activity" });
  }
}

export function startSessionHeartbeat() {
  if (started) return;
  started = true;

  connect();
  ACTIVITY_EVENTS.forEach((evt) =>
    document.addEventListener(evt, pingActivity, { passive: true })
  );
}

export function stopSessionHeartbeat() {
  if (!started) return;
  started = false;

  ACTIVITY_EVENTS.forEach((evt) =>
    document.removeEventListener(evt, pingActivity)
  );
  if (ws) {
    ws.close();
    ws = null;
  }
  currentSessionId = null;
}
